import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import { useFilterData, useLoadingData, useScheduleData, useViewData } from "../../store/magnetboardStore";
import Loader from "../../common/Loader";
import MagnetboardPeriod from "./MagnetboardPeriod";
import StudentMagnetboardRow from "./StudentMagnetboardRow";
import { getDiagnosticTimestamp, logMagnetboardDiagnostic, startMagnetboardDiagnosticTimer } from "../utils/diagnostics";

import '../../../../css/hsms/reports.css';

const INITIAL_VISIBLE_ITEMS = 20;
const LOAD_INCREMENT = 20;
const VIEWPORT_ROW_BUFFER_PX = 900;
const LIST_END_BREATHING_ROOM_PX = 160;

const MagnetboardPanel = React.memo(
  ({
    magnetboardOptions,
    scheduleData: {
      scheduleStructureData,
      setSections,
      sections,
      subsections,
      departments,
      studentMatchPercentages,
      schoolInfo,
      scheduleInfo,
      teachers,
      setTeachers,
      courses,
      classrooms,
      students,
      setStudents,
      labels,
      filteredDisplaySchedule = [],
      courseFillPercentagesByCourseId,
      studentSectionsByStudentCoursePeriod,
      studentConflictSectionLookup,
      sectionConflictSummaryBySectionId,
      sectionsById,
      subsectionParentById,
      teachersById,
      coursesById,
      classroomsById,
      labelsById,
      sectionIsAllowedForCurrentUser,
    },
    magnetboardActions: {
      updateSectionPlacement,
      toggleLockSection,
      updateManualPlacement,
      toggleEditSection,
      duplicateSectionFromMagnetboard,
      deleteSectionFromMagnetboard,
      setDuplicateLoadingTarget,
      duplicateLoadingTargets,
      setDeleteLoadingTarget,
      deleteLoadingTargets,
      toggleEditStudentSchedule,
      toggleEditData,
      togglePeriodAvailability,
    },
  }) => {
    const [selectedTeacherID, setSelectedTeacherID] = useState(null);
    const [selectedQuickViewDataID, setSelectedQuickViewDataID] = useState(null);
    const [selectedQuickViewSectionID, setSelectedQuickViewSectionID] = useState(null);
    
    // infinite scroll states
    const [visibleEndIndex, setVisibleEndIndex] = useState(INITIAL_VISIBLE_ITEMS);
    const visibleListKeyRef = useRef(null);
    const listContainerRef = useRef(null);
    const scrollParentRef = useRef(null);
    const renderCountRef = useRef(0);
    const renderStartedAt = getDiagnosticTimestamp();
    renderCountRef.current += 1;

    const { 
      currentView,
      periodsToShow,
    } = useViewData();
    const { isLoading, isSaving } = useLoadingData(); 
    const { displayCreated } = useScheduleData();
    const { filtered } = useFilterData();

    const num_cols = useMemo(() => periodsToShow.length, [periodsToShow]);
    const row_style = { gridTemplateColumns: `170px repeat(${num_cols}, 1fr)` };

    const visibleItems = useMemo(() => {
      const items = filteredDisplaySchedule.slice(0, visibleEndIndex);
      return items;
    }, [filteredDisplaySchedule, visibleEndIndex]);

    const visibleListKey = useMemo(() => {
      const endTimer = startMagnetboardDiagnosticTimer('panel-visible-list-key', {
        current_view: currentView,
        filtered_rows: filteredDisplaySchedule.length,
      });

      const visible_ids = filteredDisplaySchedule.map((entity) => `${entity.data_id || ""}`);
      const key = `${currentView || ""}:${visible_ids.join("|")}`;
      endTimer({key_length: key.length});
      return key;
    }, [currentView, filteredDisplaySchedule]);

    useEffect(() => {
      logMagnetboardDiagnostic('panel-commit', {
        render_count: renderCountRef.current,
        duration_ms: Math.round(getDiagnosticTimestamp() - renderStartedAt),
        current_view: currentView,
        display_created: displayCreated,
        is_loading: isLoading,
        is_saving: isSaving,
        filtered_rows: filteredDisplaySchedule.length,
        visible_rows: visibleItems.length,
        visible_end_index: visibleEndIndex,
        periods: periodsToShow.length,
      });
    });

    useEffect(() => {
      if (visibleListKeyRef.current === visibleListKey) return;

      visibleListKeyRef.current = visibleListKey;
      setVisibleEndIndex(Math.min(filteredDisplaySchedule.length, INITIAL_VISIBLE_ITEMS));
    }, [visibleListKey, filteredDisplaySchedule.length]);

    const getScrollParent = useCallback(() => {
      if (scrollParentRef.current && scrollParentRef.current.isConnected) {
        return scrollParentRef.current;
      }

      let current_element = listContainerRef.current?.parentElement;

      while (current_element && current_element !== document.body) {
        const current_style = window.getComputedStyle(current_element);

        if (["auto", "scroll"].includes(current_style.overflowY)) {
          scrollParentRef.current = current_element;
          return current_element;
        }

        current_element = current_element.parentElement;
      }

      scrollParentRef.current = window;
      return window;
    }, []);

    const getScrollMetrics = useCallback((scroll_parent) => {
      if (scroll_parent === window) {
        return {
          scrollTop: window.scrollY,
          viewportHeight: window.innerHeight,
          viewportTop: 0,
        };
      }

      return {
        scrollTop: scroll_parent.scrollTop,
        viewportHeight: scroll_parent.clientHeight,
        viewportTop: scroll_parent.getBoundingClientRect().top,
      };
    }, []);

    const getElementTop = useCallback((element, scroll_parent, scroll_metrics) => {
      const element_rect = element.getBoundingClientRect();

      if (scroll_parent === window) {
        return element_rect.top + scroll_metrics.scrollTop;
      }

      return element_rect.top - scroll_metrics.viewportTop + scroll_metrics.scrollTop;
    }, []);

    const getElementBottom = useCallback((element, scroll_parent, scroll_metrics) => {
      const element_rect = element.getBoundingClientRect();

      if (scroll_parent === window) {
        return element_rect.bottom + scroll_metrics.scrollTop;
      }

      return element_rect.bottom - scroll_metrics.viewportTop + scroll_metrics.scrollTop;
    }, []);

    const ensureRowsForViewport = useCallback((minimumIncrement = 0) => {
      const endTimer = startMagnetboardDiagnosticTimer('panel-ensure-rows', {
        current_view: currentView,
        minimum_increment: minimumIncrement,
        filtered_rows: filteredDisplaySchedule.length,
      });

      const totalItems = filteredDisplaySchedule.length;
      if (totalItems === 0) {
        endTimer({skipped: true});
        return;
      }

      setVisibleEndIndex((current_end_index) => {
        if (current_end_index >= totalItems) {
          endTimer({already_complete: true, current_end_index});
          return current_end_index;
        }

        let next_end_index = minimumIncrement > 0
          ? Math.min(totalItems, current_end_index + minimumIncrement)
          : current_end_index;

        const rendered_rows = Array.from(listContainerRef.current?.querySelectorAll(".mb-schedule-row-parent") || []);
        const last_rendered_row = rendered_rows[rendered_rows.length - 1];

        if (!last_rendered_row) {
          const next_index = Math.min(totalItems, Math.max(next_end_index, current_end_index + LOAD_INCREMENT));
          endTimer({next_end_index: next_index, rendered_rows: 0});
          return next_index;
        }

        const scroll_parent = getScrollParent();
        const scroll_metrics = getScrollMetrics(scroll_parent);
        const target_bottom = scroll_metrics.scrollTop + scroll_metrics.viewportHeight + VIEWPORT_ROW_BUFFER_PX;
        const last_rendered_bottom = getElementBottom(last_rendered_row, scroll_parent, scroll_metrics);

        if (last_rendered_bottom < target_bottom) {
          if (scroll_metrics.scrollTop + scroll_metrics.viewportHeight > last_rendered_bottom) {
            return totalItems;
          }

          const first_rendered_row = rendered_rows[0];
          const first_rendered_top = getElementTop(first_rendered_row, scroll_parent, scroll_metrics);
          const rendered_height = Math.max(last_rendered_bottom - first_rendered_top, rendered_rows.length);
          const average_row_height = Math.max(rendered_height / rendered_rows.length, 1);
          const extra_rows_needed = Math.ceil((target_bottom - last_rendered_bottom) / average_row_height);

          next_end_index = Math.max(next_end_index, current_end_index + extra_rows_needed + LOAD_INCREMENT);
        }

        const next_index = Math.min(totalItems, Math.max(current_end_index, next_end_index));
        endTimer({next_end_index: next_index, rendered_rows: rendered_rows.length});
        return next_index;
      });
    }, [currentView, filteredDisplaySchedule.length, getElementBottom, getElementTop, getScrollMetrics, getScrollParent]);

    const handleScroll = useCallback(() => {
      ensureRowsForViewport();
    }, [ensureRowsForViewport]);

    // Event listener for scroll
    useEffect(() => {
      let scrollFrameId = null;
      const scrollHandler = () => {
        if (scrollFrameId) return;

        scrollFrameId = window.requestAnimationFrame(() => {
          scrollFrameId = null;
          handleScroll();
        });
      };
    
      const scroll_parent = getScrollParent();
      scroll_parent.addEventListener('scroll', scrollHandler, { passive: true });
  
      return () => {
        if (scrollFrameId) window.cancelAnimationFrame(scrollFrameId);
        scroll_parent.removeEventListener('scroll', scrollHandler);
      };
    }, [getScrollParent, handleScroll]);

    useEffect(() => {
      const frame_id = window.requestAnimationFrame(() => {
        ensureRowsForViewport();
      });

      return () => {
        window.cancelAnimationFrame(frame_id);
      };
    }, [ensureRowsForViewport, visibleEndIndex]);

    useEffect(() => {
      const observerDown = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && visibleEndIndex < filteredDisplaySchedule.length) {
              ensureRowsForViewport(LOAD_INCREMENT);
            }
          });
        },
        {
          rootMargin: '200px'
        }
      );

      const timeout = setTimeout(() => {
        const loadingIndicatorDown = document.querySelector('.infinite-scroll-loader-down');
        
        if (loadingIndicatorDown) {
          observerDown.observe(loadingIndicatorDown);
        }
      }, 100);
      
      return () => {
        clearTimeout(timeout);
        observerDown.disconnect();
      };
    }, [visibleEndIndex, filteredDisplaySchedule.length, ensureRowsForViewport]);

    // Functions
    const handleSelectTeacher = useCallback((selected_teacher) => {
      setSelectedQuickViewDataID(null);
      setSelectedTeacherID(selected_teacher);
    },[])
  
    const handleSelectQuickViewDataID = useCallback((selected_data_id, selected_section_id, e) => {
      setSelectedTeacherID(null);
      setSelectedQuickViewDataID(selected_data_id);
      setSelectedQuickViewSectionID(selected_section_id);
    },[])

    return (
      <DndProvider backend={HTML5Backend}>
        <div
          className="mb-row mb-middle-header fixed-heading-on-scroll"
          style={row_style}
        >
          <div className="mb-main-inner-box capitalize">{currentView}</div>
          {periodsToShow.map((period_info, index) => {
            const period_name = period_info.period_name;

            return (
              <div className="mb-inner-box" key={index}>
                <div>{period_name}</div>
              </div>
            );
          })}
        </div>

        <div 
          className="mb-bottom-container mb-bottom-container-no-scrollbar" 
          ref={listContainerRef}
          style={{ 
            scrollbarWidth: 'none', /* Firefox */
            msOverflowStyle: 'none', /* Internet Explorer 10+ */
            marginBottom: visibleEndIndex >= filteredDisplaySchedule.length && visibleItems.length > 0 ? `${LIST_END_BREATHING_ROOM_PX}px` : '0px',
          }}
        >
          {isLoading || isSaving ? (
            <div className="mb-message-container">
              <Loader />
            </div>
          ) : displayCreated && currentView
          ? (
            <>
              {visibleItems.length === 0 && !isLoading && !isSaving && filtered && (
                <div className="mb-message-container medium-text">
                  No items match the current filters.
                </div>
              )}
              
              {visibleItems.map((entity, index) => {
                const realDataIndex = index;
                
                const RowComponent = currentView === "student" ? StudentMagnetboardRow : MagnetboardPeriod;

                return <RowComponent 
                  key={`${entity.data_id}-${realDataIndex}`}
                  entity={entity}
                  data_index={realDataIndex}
                  magnetboardOptions={magnetboardOptions}
                  selectedTeacherID={selectedTeacherID}
                  selectedQuickViewDataID={selectedQuickViewDataID}
                  selectedQuickViewSectionID={selectedQuickViewSectionID}
                  handleSelectTeacher={handleSelectTeacher}
                  handleSelectQuickViewDataID={handleSelectQuickViewDataID}
                  scheduleData={{
                    scheduleStructureData,
                    setSections,
                    sections,
                    subsections,
                    departments,
                    studentMatchPercentages,
                    schoolInfo,
                    scheduleInfo,
                    teachers,
                    setTeachers,
                    courses,
                    classrooms,
                    students,
                    setStudents,
                    labels,
                    courseFillPercentagesByCourseId,
                    studentSectionsByStudentCoursePeriod,
                    studentConflictSectionLookup,
                    sectionConflictSummaryBySectionId,
                    sectionsById,
                    subsectionParentById,
                    teachersById,
                    coursesById,
                    classroomsById,
                    labelsById,
                    sectionIsAllowedForCurrentUser,
                  }}
                  magnetboardActions={{
                    updateSectionPlacement,
                    toggleLockSection,
                    updateManualPlacement,
                    toggleEditSection,
                    duplicateSectionFromMagnetboard,
                    deleteSectionFromMagnetboard,
                    setDuplicateLoadingTarget,
                    duplicateLoadingTargets,
                    setDeleteLoadingTarget,
                    deleteLoadingTargets,
                    toggleEditStudentSchedule,
                    toggleEditData,
                    togglePeriodAvailability,
                  }}
                />
              })}
              
              {visibleEndIndex < filteredDisplaySchedule.length && (
                <div 
                  className="infinite-scroll-loader-down"
                >
                  <div className="mb-scroll-skeleton-stack">
                    <div className="mb-scroll-skeleton-row"></div>
                    <div className="mb-scroll-skeleton-row mb-scroll-skeleton-row-short"></div>
                  </div>
                </div>
              )}

            </>
          ) : null}
        </div>
      </DndProvider>
    );
  }
);

export default MagnetboardPanel;
